import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplPurposeType } from "../../../../domain/types";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
const Text = Typography;

const InternationalTplPurpose = () => {
  const { t } = useTranslation();
  const { field_Language, lang } = useAppSelector(
    (data: { app: AppState }) => data.app
  );
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedState, setEditedState] =
    useState<Partial<IntTplPurposeType> | null>(null);

  const { data: clientTypes, isLoading: isClientTypeLoading } =
    api.useGetClientTypesQuery(undefined);

  const {
    data: IntTplPurposeList,
    refetch: refetchIntTplPurpose,
    isLoading: isIntTplPurposeLoading,
  } = api.useGetIntTplPurposeQuery(undefined);

  const {
    data: intTplPurposeById,
    isLoading: isIntTplPurposeByIdLoading,
    isSuccess: intTplPurposeSuccess,
    refetch: refetchIntTplPurposeById,
  } = api.useGetIntTplPurposeByIdQuery(editedState?.id as number, {
    skip: !editedState?.id,
  });

  const _refetchPurpose = useCallback(() => {
    if (editedState?.id) {
      refetchIntTplPurposeById();
    }
  }, [editedState?.id, refetchIntTplPurposeById]);

  useEffect(() => {
    _refetchPurpose();

    return () => {};
  }, [_refetchPurpose]);

  const [createPurpose, { isLoading: isCreateIntTplPurposeLoading }] =
    api.useCreateIntTplPurposeMutation();

  const [updatePurpose, { isLoading: isUpdateIntTplPurposeLoading }] =
    api.useUpdateIntTplPurposeMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
    setEditedState(null);
  };

  const setEditedField = (field: keyof IntTplPurposeType, value: any) => {
    setEditedState({
      ...editedState,
      [field]: value,
    });
  };

  const [
    deletePurpose,
    {
      isLoading: isDeleteIntTplPurposeLoading,
      isSuccess: deleteIntTplPurposeSuccess,
    },
  ] = api.useDeleteIntTplPurposeMutation();

  const _createPurpose = async () => {
    if (editedState) {
      try {
        await createPurpose(editedState).unwrap();
        refetchIntTplPurpose();
        toast.success(`${t("purpose")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updatePurpose = async () => {
    if (editedState) {
      try {
        await updatePurpose(editedState).unwrap();
        refetchIntTplPurpose();
        toast.success(`${t("purpose")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (deleteIntTplPurposeSuccess) {
      refetchIntTplPurpose();
    }
  }, [deleteIntTplPurposeSuccess, refetchIntTplPurpose]);

  useEffect(() => {
    if (
      intTplPurposeById &&
      intTplPurposeSuccess &&
      intTplPurposeById?.id === editedState?.id
    ) {
      setEditedState(intTplPurposeById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [intTplPurposeById, intTplPurposeSuccess, editedState?.id]);

  const MainLoader = isIntTplPurposeLoading || isClientTypeLoading;

  const DrawerLoader =
    isUpdateIntTplPurposeLoading ||
    isDeleteIntTplPurposeLoading ||
    isIntTplPurposeByIdLoading ||
    isCreateIntTplPurposeLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {IntTplPurposeList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "320px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "120px",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>
                    {item.translations?.[field_Language]?.name ||
                      t("textNotFound")}
                  </Title>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedState?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
            extra={<AdminLangSwicher />}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("purpose")}</Text>
                  <Input
                    size="large"
                    placeholder={t("purpose")}
                    value={
                      editedState?.translations?.[field_Language]?.name || ""
                    }
                    onChange={(event) =>
                      setEditedField("translations", {
                        ...editedState?.translations,
                        [field_Language]: {
                          ...editedState?.translations?.[field_Language],
                          name: event?.target?.value,
                        },
                      })
                    }
                  />
                </Space>

                <Checkbox.Group
                  options={clientTypes?.map((item) => ({
                    label: item?.translations?.[lang]?.name,
                    value: item.id,
                    className: `${item.id}`,
                  }))}
                  onChange={(checkedValues) =>
                    setEditedField("clientTypeIds", checkedValues)
                  }
                  value={editedState?.clientTypeIds || []}
                />

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedState?.status || undefined}
                    onChange={(value) => [setEditedField("status", value)]}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedState?.id === 0 ? (
                    <Button
                      disabled={
                        editedState && editedState.status ? false : true
                      }
                      size="large"
                      type="primary"
                      onClick={_createPurpose}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedState && editedState.id && editedState.id !== 0 ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={_updatePurpose}
                      >
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          deletePurpose(editedState?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplPurpose;
